import { useEffect, useState } from "react";
import { Icon } from "../Icon";
import { TripIcons, PlaceMark } from "../trip/TripFormAtoms";
import { apiRequest } from "../../utils/api";
import { fillText, dateLocale, clockText } from "../../utils/leadDisplay";
import useLeadRating, { RATING_PARTS } from "../../hooks/useLeadRating";
import { PartRows } from "../detail/DetailAtoms";
import RatingMatchQuestions from "../RatingMatchQuestions";
import { useLocalNames } from "../../utils/localNames";
import { VoiceButton } from "../VoiceInput";

/* The pieces the two home lists are built from - Cars (pages/Home) and Hire Driver
   (pages/AltingDriversList) - so a customer moving between the two tabs meets one
   product: the same trip card, the same chips, the same result line and the same Call.
   They share their look with the trip forms and My Cars - white cards on a hairline
   border, Sramika green for the one thing to do.

   Sizes are written at their full design size; on a phone every button and every
   control marked .ctl renders at four-fifths of it (index.css, "Buttons and tags at
   four-fifths height"), with the rest of the app. */

/* ── Small helpers ─────────────────────────────────────────────────────────── */

export const placeText = (loc) => (typeof loc === "string" ? loc : (loc?.value || loc?.label || ""));
// "Benz Circle, Vijayawada, Andhra Pradesh, India" → "Benz Circle".
export const shortPlace = (loc) => placeText(loc).split(",")[0].trim();

/* "Today, 8:00 AM", "Tomorrow, 8:00 AM" or "Wed, 1 Oct, 8:00 AM" - with the day and
   month in the reader's own script, and the time of day in their words (ఉదయం 8:00). */
export function tripWhen(value, t, lang) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const loc = dateLocale(lang);
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const ahead = Math.round((dayStart(d) - dayStart(new Date())) / 86400000);
  const day = ahead === 0 ? t("lp_today")
    : ahead === 1 ? t("lp_tomorrow")
    : d.toLocaleDateString(loc, { weekday: "short", day: "numeric", month: "short" });
  return `${day}, ${clockText(d, lang)}`;
}

// "1 car" / "33 cars" on the Cars list, "1 driver" / "12 drivers" on Hire Driver.
const COUNT_WORDS = { cars: ["hm_oneCar", "hm_nCars"], drivers: ["hm_oneDriver", "hm_nDrivers"] };
export function countWords(n, t, kind = "cars") {
  const [one, many] = COUNT_WORDS[kind] || COUNT_WORDS.cars;
  return n === 1 ? t(one) : fillText(t(many), { n });
}

/* Opens the city list in the header from anywhere on the page - an empty list's
   "Change city", the no-city card. The header listens for it. */
export const CITY_PICKER_EVENT = "sramika:pick-city";
export const openCityPicker = () => window.dispatchEvent(new Event(CITY_PICKER_EVENT));

const svg = (children, size = 16, extra = {}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...extra}>{children}</svg>
);
// Pickup and drop change sides: two arrows, left and right.
const SwapIcon = (s) => svg(<path d="M4 8h15M15 4l4 4-4 4M20 16H5M9 12l-4 4 4 4" />, s);
// A clock turning back: something done before.
const HistoryIcon = (s) => svg(<><path d="M3 12a9 9 0 1 0 2.64-6.36L3 8" /><path d="M3 3v5h5" /><path d="M12 7.5V12l3 2" /></>, s);

/* ── Log in banner, for visitors ───────────────────────────────────────────── */

const CITY_BANNERS = { Vijayawada: { src: "/banners/vijayawada.webp", position: "top right" } };
const COMMON_BANNERS = [
  { src: "/banners/mountain_car.webp", position: "center" },
  { src: "/banners/river_car.webp", position: "center" },
];

/* What logging in gives, in one honest line - prices and the call. Nothing is booked
   in the app, so it no longer promises to book rides or track trips. */
export function GuestBanner({ city, title, sub, cta, onLogin }) {
  const [index, setIndex] = useState(0);
  const banners = city && CITY_BANNERS[city] ? [CITY_BANNERS[city], ...COMMON_BANNERS] : COMMON_BANNERS;

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % banners.length), 4500);
    return () => clearInterval(timer);
  }, [banners.length]);
  useEffect(() => { setIndex(0); }, [city]);

  return (
    <section className="relative overflow-hidden rounded-[18px] bg-[#111827] shadow-md">
      {banners.map((b, i) => (
        <img key={b.src} src={b.src} alt="" aria-hidden="true" style={{ objectPosition: b.position }}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220]/95 via-[#0B1220]/75 to-[#3F5E00]/30" />
      <div className="relative z-10 px-3.5 py-3 max-w-[88%]">
        <p className="text-[12.5px] font-extrabold text-white leading-snug">{title}</p>
        <p className="text-[9.5px] text-white/85 leading-snug mt-1">{sub}</p>
        <button type="button" onClick={onLogin}
          className="mt-2.5 min-h-[40px] px-4 rounded-[11px] bg-[#7BAE00] text-white text-[11px] font-bold shadow-[0_4px_12px_rgba(123,174,0,0.35)] active:scale-[0.98]">
          {cta}
        </button>
      </div>
    </section>
  );
}

/* The same, small, among the cars (mock Home_1 change 6): a white card under the third
   car, like "Do you own a car?" under the last. Call still asks for the login. */
export function GuestCard({ title, sub, cta, onLogin }) {
  return (
    <section className="rounded-2xl bg-white border border-[#E5E7EB] p-3 flex items-center gap-3 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
      <span className="w-[34px] h-[34px] rounded-[10px] bg-[#F1F7E3] text-[#5E8700] flex items-center justify-center shrink-0">
        <Icon name="phone" size={17} strokeWidth={2.2} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11.5px] font-bold text-[#1F2937] leading-tight">{title}</p>
        <p className="text-[9.5px] text-[#64748B] leading-snug mt-0.5">{sub}</p>
      </div>
      <button type="button" onClick={onLogin}
        className="shrink-0 min-h-[40px] px-4 rounded-[11px] bg-[#7BAE00] text-white text-[11px] font-bold active:scale-[0.98]">
        {cta}
      </button>
    </section>
  );
}

/* ── The trip ──────────────────────────────────────────────────────────────── */

const CARD = "bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_10px_rgba(15,23,42,0.04)]";



/* The trip: pickup and drop side by side - each opening the trip form - with a swap
   between them, and one line under them once the trip is set: when, which kind and how
   far. Before that there is no line: "Set your trip to see each car's / driver's fare"
   went from both lists at the owner's request (Oct 2026). */
export function WhereCard({ t, from, to, meta, onOpen, onSwap, recent = [] }) {
  // Places in the reader's script; what the form holds stays as it was picked.
  const ln = useLocalNames();
  const place = (raw, label, marker) => {
    const text = ln(raw, "place");
    return (
    <button type="button" onClick={onOpen} aria-label={text ? `${label}: ${text}` : label}
      className="flex-1 min-w-0 min-h-[50px] rounded-xl border border-[#E5E7EB] bg-white px-2.5 flex items-center gap-2 text-left active:scale-[0.99]">
      {marker}
      <span className="min-w-0 flex-1">
        {text ? (
          <>
            <span className="block text-[9px] font-medium text-[#64748B] leading-tight truncate">{label}</span>
            <span className="block text-[11.5px] font-bold text-[#0F172A] leading-tight truncate mt-[2px]">{text}</span>
          </>
        ) : (
          <span className="block text-[10.5px] font-semibold text-[#94A3B8] leading-tight whitespace-nowrap truncate">{label}</span>
        )}
      </span>
    </button>
    );
  };

  return (
    <section className={`${CARD} p-2`}>
      <div className="flex items-center gap-1">
        {place(from, t("lp_lblPickup"), <PlaceMark kind="pickup" size={24} />)}
        <button type="button" onClick={onSwap} disabled={!from && !to} aria-label={t("lp_swap")} title={t("lp_swap")}
          className="shrink-0 w-[34px] h-[34px] rounded-full flex items-center justify-center text-[#475569] active:bg-[#F1F5F9] disabled:opacity-40">
          {SwapIcon(18)}
        </button>
        {place(to, t("lp_lblDrop"), <PlaceMark kind="drop" size={24} />)}
      </div>
      {meta && (
        <div className="mt-2 rounded-[10px] bg-[#F4F9EA] border border-[#DDEBC2] px-2.5 py-2 flex items-center gap-2">
          <span className="shrink-0 text-[#3F5E00]">{TripIcons.calendar(14)}</span>
          <span className="min-w-0 text-[10px] font-medium text-[#2F4700] leading-snug">{meta}</span>
        </div>
      )}
      {recent.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-2 px-2 pb-0.5">
          <span className="shrink-0 text-[9.5px] font-bold text-[#64748B]">{t("hm_recent")}</span>
          {recent.map((r) => (
            <button key={r.key} type="button" onClick={r.onClick}
              className="shrink-0 min-h-[36px] max-w-[220px] px-3 rounded-full border border-[#E5E7EB] bg-white flex items-center gap-1.5 text-[10px] font-semibold text-[#334155] active:scale-[0.98]">
              <span className="text-[#5E8700] shrink-0">{TripIcons.pin(12)}</span>
              <span className="truncate">{ln(r.label, "place")}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/* ── Chips ─────────────────────────────────────────────────────────────────── */

/* A chip - with options, the phone's own picker laid over it; without, a button.
   Green when it is doing something, with an × to undo it. Marked .ctl so it renders at
   the same four-fifths height as the buttons around it. */
export function QuickChip({ icon, label, active, value, options, onChange, onClick, onClear, clearLabel, pickLabel }) {
  const clearable = active && onClear;
  return (
    <div className={`ctl relative shrink-0 h-[40px] rounded-full border flex items-center gap-1.5 text-[11px] font-bold whitespace-nowrap transition-colors ${
      clearable ? "pl-3 pr-1" : "px-3"} ${active ? "bg-[#F1F7E3] border-[#7BAE00] text-[#3F5E00]" : "bg-white border-[#E5E7EB] text-[#334155]"}`}>
      <span className="text-[#5E8700] shrink-0">{icon}</span>
      <span>{label}</span>
      {!clearable && <span className="text-[#94A3B8] shrink-0"><Icon name="chevronDown" size={12} strokeWidth={2.4} /></span>}
      {options && (
        <select aria-label={pickLabel || label} value={value} onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer">
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {!options && onClick && (
        <button type="button" onClick={onClick} aria-label={pickLabel || label} className="absolute inset-0 w-full h-full rounded-full" />
      )}
      {clearable && (
        <button type="button" aria-label={clearLabel} title={clearLabel}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClear(); }}
          className="relative z-10 w-[32px] h-[32px] rounded-full flex items-center justify-center text-[#3F5E00] active:bg-[#E3F0C4]">
          <Icon name="x" size={13} strokeWidth={2.6} />
        </button>
      )}
    </div>
  );
}

/* The row the chips sit in. It scrolls sideways when a language needs the room, and
   runs to the screen's edges so a chip half off the edge says there are more. */
export function ChipRow({ children }) {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-3 px-3 py-0.5">
      {children}
    </div>
  );
}

/* ── What the list is showing ──────────────────────────────────────────────── */

/* The list's count is the section's title - "70 cars in Vijayawada", bold, with Share
   list beside it, no box (mock Home_1 change 5). A strip with an icon is a notice
   ("near Gollapudi - set as pickup?") and keeps its light green box: one low line,
   28px, growing to fit a button or a second line. */
export function ResultStrip({ title, sub, link, action, actionLabel, icon, share, shareLabel }) {
  if (!icon) {
    return (
      <div className="flex items-center gap-2.5 px-0.5 min-h-[34px]">
        <div className="flex-1 min-w-0">
          <h2 className="text-[13px] font-extrabold text-[#0F172A] leading-snug">{title}</h2>
          {sub && <p className="text-[9.5px] font-medium text-[#64748B] leading-snug mt-0.5">{sub}</p>}
          {link}
        </div>
        {action && (
          <button type="button" onClick={action}
            className="shrink-0 min-h-[36px] px-3 rounded-full bg-[#7BAE00] text-white text-[10.5px] font-bold active:scale-[0.97]">
            {actionLabel}
          </button>
        )}
        {!action && share && (
          <button type="button" onClick={share}
            className="shrink-0 min-h-[34px] px-3 rounded-full border border-[#D6E6B0] bg-white text-[#3F5E00] text-[10px] font-extrabold flex items-center gap-1.5 active:scale-[0.97]">
            <Icon name="share" size={12} strokeWidth={2.4} />{shareLabel}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 rounded-[14px] bg-[#F4F9EA] border border-[#DDEBC2] px-3 py-1 min-h-[28px]">
      {icon
        ? <span className="text-[#5E8700] shrink-0">{icon}</span>
        : <span className="w-2 h-2 rounded-full bg-[#7BAE00] shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-[10.5px] font-bold text-[#1F2937] leading-snug">{title}</p>
        {sub && <p className="text-[9.5px] font-medium text-[#475569] leading-snug mt-0.5">{sub}</p>}
        {link}
      </div>
      {action && (
        <button type="button" onClick={action}
          className="shrink-0 min-h-[36px] px-3 rounded-full bg-[#7BAE00] text-white text-[10.5px] font-bold active:scale-[0.97]">
          {actionLabel}
        </button>
      )}
      {/* The list as a link - the trip is in the address (utils/tripLink, mock Fix_A change 10). */}
      {!action && share && (
        <button type="button" onClick={share}
          className="shrink-0 min-h-[32px] px-2.5 rounded-full border border-[#7BAE00] bg-white text-[#3F5E00] text-[10px] font-extrabold flex items-center gap-1 active:scale-[0.97]">
          <Icon name="share" size={12} strokeWidth={2.4} />{shareLabel}
        </button>
      )}
    </div>
  );
}

/* A message in place of the list: an icon, a line, a line under it, and what to do. */
export function ListMessage({ icon, title, text, children }) {
  return (
    <div className={`${CARD} px-4 py-6 text-center`}>
      <span className="w-12 h-12 rounded-full bg-[#F1F7E3] text-[#5E8700] flex items-center justify-center mx-auto">{icon}</span>
      <p className="mt-3 text-[12.5px] font-extrabold text-[#0F172A] leading-snug">{title}</p>
      {text && <p className="mt-1 text-[10px] text-[#64748B] leading-snug max-w-[300px] mx-auto">{text}</p>}
      {children && <div className="mt-4 flex flex-wrap items-center justify-center gap-2">{children}</div>}
    </div>
  );
}

export const PRIMARY_BTN = "min-h-[44px] px-5 rounded-xl bg-[#7BAE00] text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98]";
export const SECONDARY_BTN = "min-h-[44px] px-5 rounded-xl border border-[#CBD5E1] bg-white text-[#334155] text-[11.5px] font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98]";

export function Loading({ text }) {
  return (
    <div className="flex items-center justify-center gap-2 py-8">
      <span className="w-5 h-5 border-2 border-[#7BAE00] border-t-transparent rounded-full animate-spin" />
      <p className="text-[11px] text-[#64748B]">{text}</p>
    </div>
  );
}

/* ── Your recent enquiry ───────────────────────────────────────────────────── */

/* The customer's own enquiries, newest first - the list the History tab shows. Asked
   once, and only when someone is signed in. */
export function useMyRecentLeads(enabled) {
  const [state, setState] = useState({ leads: [], pending: [] });
  useEffect(() => {
    if (!enabled) { setState({ leads: [], pending: [] }); return undefined; }
    let alive = true;
    apiRequest("/leads/my-recent")
      .then((res) => { if (alive) setState({ leads: res?.data || [], pending: res?.pending || [] }); })
      // Quiet: without enquiries there is simply nothing to show.
      .catch(() => {});
    return () => { alive = false; };
  }, [enabled]);
  return state;
}

/* Shown once. The first visit that shows an enquiry marks it seen (localStorage) and
   keeps showing it for the rest of that visit (sessionStorage), so moving to a car and
   back does not make it vanish; the next visit, or the next login, does not show it
   again. A newer enquiry is a new card, shown once in its turn. */
const SEEN_KEY = (leadId) => `sramika_enquiry_seen_${leadId}`;
const SHOWING_KEY = (leadId) => `sramika_enquiry_showing_${leadId}`;

/* The newest enquiry on this list - cars or hire drivers - unless it is already seen,
   or it went nowhere: an enquiry either side marked "not booked" is not one to call
   again. Only the newest; an older one is not brought back in its place. */
export function recentEnquiryOf(leads, hire = false) {
  const lead = (leads || []).find((l) => l?.carId && (l.sourceType === "HIRE_DRIVER") === hire);
  if (!lead || (lead.partnerOutcome || lead.customerOutcome) === "LOST") return null;
  try {
    if (localStorage.getItem(SEEN_KEY(lead.leadId)) && !sessionStorage.getItem(SHOWING_KEY(lead.leadId))) return null;
  } catch { /* storage unavailable: show it */ }
  return lead;
}

export function markEnquirySeen(lead) {
  try {
    if (!localStorage.getItem(SEEN_KEY(lead.leadId))) {
      localStorage.setItem(SEEN_KEY(lead.leadId), new Date().toISOString());
      sessionStorage.setItem(SHOWING_KEY(lead.leadId), "1");
    }
  } catch { /* storage unavailable */ }
}

// × - gone now, not only from the next visit.
export function hideEnquiry(lead) {
  try {
    localStorage.setItem(SEEN_KEY(lead.leadId), new Date().toISOString());
    sessionStorage.removeItem(SHOWING_KEY(lead.leadId));
  } catch { /* storage unavailable */ }
}

const firstNameOf = (name) => {
  const w = String(name || "").trim().split(/\s+/)[0] || "";
  return w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : "";
};

const Star = ({ on, size }) => (
  <Icon name="star" size={size} fill={on ? "#F59E0B" : "none"} stroke={on ? "#F59E0B" : "#CBD5E1"} strokeWidth={1.8} />
);

/* "Your recent enquiry": the route, what was enquired about - the car, or the driver
   for a hire - and the stars for it, with a line of feedback. The same stars the
   enquiry page asks for (hooks/useLeadRating): given here, they are not asked again
   there, and one given there shows here as thanks. */
export function RecentEnquiryCard({ lead, t, onHide }) {
  useEffect(() => { markEnquirySeen(lead); }, [lead]);
  // A car is rated in its five parts too, as on its page (mock Rate5_1 change 2); a hire as before.
  const carParts = lead?.sourceType !== "HIRE_DRIVER" ? RATING_PARTS : null;
  const rate = useLeadRating(lead, t, { withParts: !!carParts });
  const { myRating, stars, pick, review, setReview, busy, error, send } = rate;
  const ln = useLocalNames();
  const route = ln([shortPlace(lead.pickupLocation), shortPlace(lead.dropLocation)].filter(Boolean).join(" → "), "place");
  const what = lead.carLabel ? ln(lead.carLabel, "car") : ln(firstNameOf(lead.partnerName), "person");
  return (
    <section className="relative rounded-2xl border border-[#DDEBC2] bg-[#F4F9EA] p-2.5">
      <div className="flex items-center gap-2.5 pr-6">
        <span className="w-[36px] h-[36px] rounded-xl bg-[#E6F1CF] text-[#3F5E00] flex items-center justify-center shrink-0">{HistoryIcon(18)}</span>
        {/* The route on one line and the car on the next, so neither is the part cut. */}
        <div className="flex-1 min-w-0">
          <p className="text-[10.5px] font-extrabold text-[#3F5E00] leading-tight line-clamp-2">{t("hm_recentEnquiry")}</p>
          {route && <p className="text-[10.5px] font-bold text-[#0F172A] leading-snug mt-0.5 truncate">{route}</p>}
          {what && <p className="text-[9.5px] text-[#64748B] leading-snug truncate">{what}</p>}
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-[#DDEBC2]">
        {myRating ? (
          <div className="flex items-center gap-2 flex-wrap min-h-[30px]">
            <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} on={n <= myRating} size={16} />)}</span>
            <span className="text-[10px] font-semibold text-[#3F5E00]">{fillText(t("ld_rateThanks"), { n: myRating })}</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-x-2 gap-y-1 flex-wrap">
              <p className="text-[10.5px] font-bold text-[#1F2937] leading-tight">{t("ld_rateTitle")}</p>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => pick(n)} aria-label={`${n} ★`} aria-pressed={n <= stars}
                    className="w-[38px] h-[38px] flex items-center justify-center active:scale-95">
                    <Star on={n <= stars} size={24} />
                  </button>
                ))}
              </div>
            </div>
            {stars > 0 && <PartRows t={t} rate={rate} partKeys={carParts} />}
            {/* Same car, same driver - asked once there are stars, either can be left. */}
            {stars > 0 && <RatingMatchQuestions t={t} rate={rate} />}
            {/* The line of feedback appears once there are stars to send with it. */}
            {stars > 0 && (
              <div className="mt-1.5 flex items-stretch gap-2">
                <input value={review} maxLength={300} onChange={(e) => setReview(e.target.value.slice(0, 300))}
                  placeholder={t("ld_ratePh")} aria-label={t("ld_ratePh")}
                  className="ctl flex-1 min-w-0 h-[42px] px-3 rounded-xl border border-[#DDEBC2] bg-white text-[11px] text-[#1F2937] placeholder:text-[#94A3B8] outline-none focus:border-[#7BAE00]" />
                <VoiceButton where="feedback_home" className="w-[42px] h-[42px]" onText={(v) => setReview((r) => `${r ? `${r} ` : ""}${v}`.slice(0, 300))} />
                <button type="button" onClick={send} disabled={busy}
                  className="shrink-0 h-[42px] px-3.5 rounded-xl bg-[#7BAE00] text-white text-[10.5px] font-bold shadow-[0_2px_6px_rgba(123,174,0,0.3)] disabled:opacity-50 active:scale-[0.98]">
                  {t("ld_rateSend")}
                </button>
              </div>
            )}
            {error && <p className="text-[9.5px] font-bold text-[#DC2626] mt-1.5">{error}</p>}
          </>
        )}
      </div>

      {/* × in the corner, out of the row, so the route keeps its width. */}
      <button type="button" onClick={() => onHide(lead)} aria-label={t("hm_hide")} title={t("hm_hide")}
        className="absolute top-0.5 right-0.5 w-[30px] h-[30px] rounded-full flex items-center justify-center text-[#64748B] active:bg-[#E6F1CF]">
        <Icon name="x" size={14} strokeWidth={2.4} />
      </button>
    </section>
  );
}

/* ── Before a call ─────────────────────────────────────────────────────────── */

/* Every Call files an enquiry in the partner's record, and in a list the next car is a
   thumb away - so the list asks once, in the reader's language, with the two answers
   as big as the question. */
export function CallConfirmSheet({ title, text, callLabel, cancelLabel, onCall, onCancel }) {
  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center" role="dialog" aria-modal="true">
      <button type="button" aria-label={cancelLabel} onClick={onCancel} className="absolute inset-0 w-full h-full bg-[rgba(15,23,42,0.45)] cursor-default" />
      <div className="relative w-full max-w-lg bg-white rounded-t-[22px] px-4 pt-3 shadow-[0_-8px_30px_rgba(0,0,0,0.18)]"
        style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}>
        <div className="w-10 h-1 rounded-full bg-[#E2E8F0] mx-auto mb-3" />
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-full bg-[#F1F7E3] text-[#5E8700] flex items-center justify-center shrink-0">
            <Icon name="phone" size={20} strokeWidth={2} />
          </span>
          <div className="min-w-0 pt-0.5">
            <p className="text-[14px] font-extrabold text-[#0F172A] leading-snug">{title}</p>
            {text && <p className="text-[10.5px] text-[#64748B] leading-snug mt-1">{text}</p>}
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={onCancel}
            className="flex-1 min-h-[48px] rounded-xl border border-[#CBD5E1] bg-white text-[12px] font-bold text-[#334155] active:scale-[0.98]">
            {cancelLabel}
          </button>
          <button type="button" onClick={onCall}
            className="flex-[2] min-h-[48px] rounded-xl bg-[#7BAE00] text-white text-[12px] font-bold flex items-center justify-center gap-2 shadow-md active:scale-[0.98]">
            <Icon name="phone" size={16} stroke="#FFFFFF" fill="#FFFFFF" strokeWidth={1.4} />
            {callLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
